import {useId, useState} from "react";
import {ReactIsland} from "@/shared/reactIsland";
import {AlertDialog} from "@/primitives/AlertDialog";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {Notify} from "@/shared/ui/notify";
import {api} from "@/shared/api";
import { SecretInputGroup } from "@/components/SecretInputGroup";

/** The endpoint the token authenticates against, shown so admins can hand it to whoever wires up the integration. */
const SOAP_API_ENDPOINT = "timrsync.timr.com/timrsync";

export interface SoapApiPanelProps {
	timrSyncToken: string | null;
}

export function SoapApiPanel({timrSyncToken}: SoapApiPanelProps) {
	const [token, setToken] = useState<string | null>(timrSyncToken);
	const [busy, setBusy] = useState(false);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const tokenId = useId();

	/*
	 * don´t mount the panel when no token exists
	 * also hide the panel after deletion
	*/
	if (token == null) {
		return null;
	}

	const generate = () => {
		setBusy(true);
		api("/admin/generateTimrSyncToken.json")
			.post()
			.done((response) => setToken(response.timrSyncToken))
			.always(() => setBusy(false));
	};

	const remove = () => {
		setBusy(true);
		api("/admin/deleteTimrSyncToken.json")
			.post()
			.done(() => {
				setToken(null);
				Notify.success(messages["admin.clientSettings.soapApi.deleted"]);
			})
			.always(() => setBusy(false));
	};

	return (
		<div className="panel panel-flat">
			<div className="panel-heading">
				<h6 className="panel-title">{messages["admin.clientSettings.soapApi"]}</h6>
				<samp className="text-muted">{SOAP_API_ENDPOINT}</samp>
			</div>
			<div className="panel-body">
				<div className="form-horizontal">
					<div className="form-group">
						<label className="control-label col-lg-3" htmlFor={tokenId}>
							{messages["admin.clientSettings.api.timrSyncToken"]}
						</label>
						<div className="col-lg-9">
							<SecretInputGroup id={tokenId} value={token}/>
						</div>
					</div>
					<div className="text-right">
						<Button variant="outline" onClick={generate} disabled={busy}>
							<FA6Icon icon="arrows-rotate" variant="regular" fixedWidth className="mr-5"/>
							{messages["dialog.generate"]}
						</Button>
						<Button
							variant="destructive"
							className="ml-5"
							onClick={() => setConfirmingDelete(true)}
							disabled={busy}
						>
							<FA6Icon icon="trash-can" variant="regular" fixedWidth className="mr-5"/>
							{messages["dialog.delete"]}
						</Button>
					</div>
				</div>
			</div>

			<AlertDialog.Root open={confirmingDelete} onOpenChange={setConfirmingDelete}>
				<AlertDialog.Content variation="danger" size="md" showCloseIcon backdropClose escapeClose>
					<AlertDialog.Header>
						<AlertDialog.Title>{messages["admin.clientSettings.soapApi.delete.title"]}</AlertDialog.Title>
					</AlertDialog.Header>
					<AlertDialog.Body>
						{/* inner html because these locales contain markup */}
						<p dangerouslySetInnerHTML={{__html: messages["admin.clientSettings.soapApi.delete.warning"]}}/>
						<p dangerouslySetInnerHTML={{__html: messages["admin.clientSettings.soapApi.delete.renewHint"]}}/>
						<p dangerouslySetInnerHTML={{__html: messages["admin.clientSettings.soapApi.delete.confirmHint"]}}/>
					</AlertDialog.Body>
					<AlertDialog.Footer>
						<AlertDialog.Close render={<Button type="button" variant="secondary"/>}>
							{messages["dialog.cancel"]}
						</AlertDialog.Close>
						<Button
							variant="destructive"
							onClick={() => {
								setConfirmingDelete(false);
								remove();
							}}
						>
							{messages["dialog.delete"]}
						</Button>
					</AlertDialog.Footer>
				</AlertDialog.Content>
			</AlertDialog.Root>
		</div>
	);
}

/* dont warn here if no mount point exists because the mount point only exists if the account has a token */
export function mountSoapApiPanelIsland(
	mountId: string,
	timrSyncToken: string | null,
): ReactIsland<SoapApiPanelProps> | undefined {
	const mount = document.getElementById(mountId);
	if (!mount) {
		return undefined;
	}

	return new ReactIsland<SoapApiPanelProps>(SoapApiPanel, {timrSyncToken}, mount);
}
